import { useEffect, useState } from "react";
import { api } from "@/client/lib/api";
import { Button } from "@/client/components/ui/button";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { Checkbox } from "@/client/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectPopup, SelectItem,
} from "@/client/components/ui/select";
import { useAdminSaveToast } from "@/client/components/admin/AdminSaveToast";
import {
  CheckCircle2Icon, Loader2Icon, AlertCircleIcon, PlusIcon, Trash2Icon,
} from "lucide-react";

// ─── Stable label maps (codes are seeded enums; we only map for display) ───
const ENROLMENT_MODE_LABELS: Record<string, string> = {
  vast: "Vast programma", keuze: "Keuzeprogramma", technohub: "TechnoHub", deskundigheidsbevordering: "Deskundigheidsbevordering", maatwerk: "Maatwerk",
};
const DECISION_STYLE: { code: string; label: string }[] = [
  { code: "active_choice", label: "Actieve keuze (school kiest zelf)" },
  { code: "confirm_decline", label: "Bevestigen of afwijzen (vooraf gekoppeld)" },
];
const DATE_PREFERENCE_POLICY: { code: string; label: string }[] = [
  { code: "none", label: "Geen datumvoorkeur" },
  { code: "optional", label: "Datumvoorkeur optioneel" },
  { code: "required", label: "Datumvoorkeur verplicht" },
];
const ADDON_POLICY: { code: string; label: string }[] = [
  { code: "none", label: "Geen extra's" },
  { code: "optional", label: "Extra's optioneel" },
];
const DISCIPLINE_LABELS: Record<string, string> = {
  theater: "Theater", muziek: "Muziek", beeldende_kunst: "Beeldende kunst", dans: "Dans",
  literatuur: "Literatuur", erfgoed: "Erfgoed", media: "Media",
};
const NO_CHOICE_SET = "__none__";

const ylShort = (code: string) => code.replace("groep_", "");
const labelFromMap = (map: Record<string, string>, code?: string | null) =>
  (code && map[code]) || code || undefined;

// ─── Dynamic multi-input row types (Datums & extra's) ───
type DateBlockRow = { startDate: string; endDate: string; label: string };
type AddonRow = { addonCode: string; label: string; description: string };

// Slugify a label into a stable-ish addon code (fallback to index when empty).
function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD") // strip diacritics
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// ─── Small presentational helpers (mirrors ActivityWizard's Field/Textarea) ───
function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4 rounded-lg border p-5">
      <div>
        <h2 className="text-sm font-semibold">{title}</h2>
        {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
      </div>
      {children}
    </section>
  );
}
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs outline-none ring-ring placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-background"
    />
  );
}
function CheckRow({ id, checked, onChange, label, hint }: {
  id: string; checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string;
}) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} className="mt-0.5" />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      </span>
    </label>
  );
}

// Year-level toggle grid (groep_1..groep_8), reused for the three leerjaar pickers.
function YearGrid({ all, selected, onToggle }: {
  all: string[]; selected: string[]; onToggle: (code: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {all.map((code) => {
        const on = selected.includes(code);
        return (
          <button
            key={code}
            type="button"
            onClick={() => onToggle(code)}
            className={`flex size-10 items-center justify-center rounded-md border text-sm font-medium transition-colors ${
              on ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent/40"
            }`}
          >
            {ylShort(code)}
          </button>
        );
      })}
    </div>
  );
}

// Scrollable checkbox list of schools, reused for the whitelist + blacklist pickers.
function SchoolMultiselect({ schools, selected, onToggle }: {
  schools: { id: number; name: string }[];
  selected: number[];
  onToggle: (id: number) => void;
}) {
  if (!schools.length) {
    return <p className="text-sm text-muted-foreground">Er zijn nog geen scholen om te kiezen.</p>;
  }
  return (
    <div className="max-h-56 space-y-1.5 overflow-y-auto rounded-md border p-3">
      {schools.map((s) => {
        const on = selected.includes(s.id);
        const cbId = `school-${s.id}`;
        return (
          <label key={s.id} htmlFor={cbId} className="flex cursor-pointer items-center gap-2.5">
            <Checkbox id={cbId} checked={on} onCheckedChange={() => onToggle(s.id)} />
            <span className="text-sm">{s.name}</span>
          </label>
        );
      })}
    </div>
  );
}

async function fetchFull(id: string) {
  const res = await (api.api.admin.programmes as any)[":id"].full.$get({ param: { id } });
  if (!res.ok) return { ok: false as const, status: res.status };
  return { ok: true as const, data: (await res.json()) as any };
}

type SaveState = "idle" | "saving" | "saved" | "error";

/**
 * Reusable editor for one activity's full configuration. Rendered both inside a dialog
 * (the primary UX, from the Aanbod list) and inside a page wrapper (the fallback
 * /admin/programmes/:id/edit route). It owns its own load + save lifecycle.
 *
 * - `programmeId` — which activity to load/edit.
 * - `onClose`     — called when the user backs out (dialog: closes it; page: navigates back).
 * - `onSaved`     — called after a successful save (dialog: refresh the list + close).
 */
export function ActivityEditForm({
  programmeId, onClose, onSaved,
}: {
  programmeId: string;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [data, setData] = useState<any | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const savedToast = useAdminSaveToast("Activiteit opgeslagen");

  // ─── Form state (pre-filled from the fetched config) ───
  // Programme display
  const [title, setTitle] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [longDescription, setLongDescription] = useState("");
  const [imagePath, setImagePath] = useState("");
  const [periodText, setPeriodText] = useState("");
  const [durationText, setDurationText] = useState("");
  const [locationText, setLocationText] = useState("");
  const [coreGoals, setCoreGoals] = useState(""); // comma-separated ints in the UI
  const [isNew, setIsNew] = useState(false);
  const [identitySensitive, setIdentitySensitive] = useState(false);
  const [oncePerYear, setOncePerYear] = useState(false);
  const [themaSpecifiek, setThemaSpecifiek] = useState(false);
  const [isActive, setIsActive] = useState(true);
  // Offering
  const [enrolmentModeCode, setEnrolmentModeCode] = useState("");
  const [decisionStyleCode, setDecisionStyleCode] = useState("");
  const [datePreferencePolicyCode, setDatePreferencePolicyCode] = useState("");
  const [addonPolicyCode, setAddonPolicyCode] = useState("");
  const [selectionPrompt, setSelectionPrompt] = useState("");
  const [offeringYearLevels, setOfferingYearLevels] = useState<string[]>([]);
  const [participatingYearLevels, setParticipatingYearLevels] = useState<string[]>([]);
  const [requiredYearLevels, setRequiredYearLevels] = useState<string[]>([]);
  const [maxYearLevels, setMaxYearLevels] = useState<string[]>([]);
  const [vastStackable, setVastStackable] = useState(false);
  const [techKwadraat, setTechKwadraat] = useState(false);
  const [disciplines, setDisciplines] = useState<string[]>([]);
  const [choiceSetId, setChoiceSetId] = useState<string>(NO_CHOICE_SET);
  // Visibility per school: whitelist (allow) + blacklist (block). School ids.
  // `restrictVisibility` gates both lists behind a single checkbox. Default: off (visible to
  // all). On load it is turned on when the activity already has any allow/block schools, so an
  // existing whitelist/blacklist stays visible. Toggling off keeps the in-memory selection but
  // we only persist the lists when it's on (see handleSave).
  const [restrictVisibility, setRestrictVisibility] = useState(false);
  const [allowSchoolIds, setAllowSchoolIds] = useState<number[]>([]);
  const [blockSchoolIds, setBlockSchoolIds] = useState<number[]>([]);
  // Date blocks: the periods the activity runs (a school expresses a voorkeur within these).
  const [dateBlocks, setDateBlocks] = useState<DateBlockRow[]>([]);
  // Add-ons: textual extras the school later ticks.
  const [addons, setAddons] = useState<AddonRow[]>([]);

  useEffect(() => {
    if (!programmeId) return;
    let alive = true;
    (async () => {
      const r = await fetchFull(programmeId);
      if (!alive) return;
      if (!r.ok) {
        setLoadError(r.status === 404 ? "Deze activiteit bestaat niet." : "Kan de activiteit niet laden.");
        setLoading(false);
        return;
      }
      const d = r.data;
      const p = d.programme;
      const o = d.offering;
      setTitle(p.title ?? "");
      setShortDescription(p.shortDescription ?? "");
      setLongDescription(p.longDescription ?? "");
      setImagePath(p.imagePath ?? "");
      setPeriodText(p.periodText ?? "");
      setDurationText(p.durationText ?? "");
      setLocationText(p.locationText ?? "");
      setCoreGoals(Array.isArray(p.coreGoals) ? p.coreGoals.join(", ") : "");
      setIsNew(!!p.isNew);
      setIdentitySensitive(!!p.identitySensitive);
      setOncePerYear(!!p.oncePerYear);
      setThemaSpecifiek(!!p.themaSpecifiek);
      setIsActive(!!p.isActive);
      if (o) {
        setEnrolmentModeCode(o.enrolmentModeCode ?? "");
        setDecisionStyleCode(o.decisionStyleCode ?? "");
        setDatePreferencePolicyCode(o.datePreferencePolicyCode ?? "");
        setAddonPolicyCode(o.addonPolicyCode ?? "");
        setSelectionPrompt(o.selectionPrompt ?? "");
        setParticipatingYearLevels(Array.isArray(o.participatingYearLevels) ? o.participatingYearLevels : []);
        setRequiredYearLevels(Array.isArray(o.requiredYearLevels) ? o.requiredYearLevels : []);
        setMaxYearLevels(Array.isArray(o.maxYearLevels) ? o.maxYearLevels : []);
        setVastStackable(!!o.vastStackable);
        setTechKwadraat(!!o.techKwadraat);
      }
      setOfferingYearLevels(Array.isArray(d.offeringYearLevels) ? d.offeringYearLevels : []);
      setDisciplines(Array.isArray(d.disciplines) ? d.disciplines : []);
      setChoiceSetId(d.choiceSetId != null ? String(d.choiceSetId) : NO_CHOICE_SET);
      setDateBlocks(
        Array.isArray(d.dateBlocks)
          ? d.dateBlocks.map((b: any) => ({
              startDate: b.startDate ?? "",
              endDate: b.endDate ?? "",
              label: b.label ?? "",
            }))
          : []
      );
      setAddons(
        Array.isArray(d.addons)
          ? d.addons.map((a: any) => ({
              addonCode: a.addonCode ?? "",
              label: a.label ?? "",
              description: a.description ?? "",
            }))
          : []
      );
      const allow = Array.isArray(d.schoolAllowSchoolIds) ? d.schoolAllowSchoolIds : [];
      const block = Array.isArray(d.schoolBlockSchoolIds) ? d.schoolBlockSchoolIds : [];
      setAllowSchoolIds(allow);
      setBlockSchoolIds(block);
      // Default the toggle on when there's already a whitelist or blacklist, so existing
      // restrictions stay visible (e.g. Dansochtend's whitelist). Otherwise default off.
      setRestrictVisibility(allow.length > 0 || block.length > 0);
      setData(d);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [programmeId]);

  function toggle(list: string[], set: (v: string[]) => void, code: string) {
    set(list.includes(code) ? list.filter((c) => c !== code) : [...list, code]);
  }

  function toggleId(list: number[], set: (v: number[]) => void, idVal: number) {
    set(list.includes(idVal) ? list.filter((x) => x !== idVal) : [...list, idVal]);
  }

  // ─── Date-block row helpers ───
  const addDateBlock = () => setDateBlocks((rows) => [...rows, { startDate: "", endDate: "", label: "" }]);
  const removeDateBlock = (i: number) => setDateBlocks((rows) => rows.filter((_, idx) => idx !== i));
  const updateDateBlock = (i: number, patch: Partial<DateBlockRow>) =>
    setDateBlocks((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  // ─── Add-on row helpers ───
  const addAddon = () => setAddons((rows) => [...rows, { addonCode: "", label: "", description: "" }]);
  const removeAddon = (i: number) => setAddons((rows) => rows.filter((_, idx) => idx !== i));
  const updateAddon = (i: number, patch: Partial<AddonRow>) =>
    setAddons((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  async function handleSave() {
    if (!programmeId || !data) return;
    setSaveState("saving");
    setSaveError(null);

    const hasOffering = !!data.offering;

    // Normalise core goals: comma-separated ints; empty string clears to null.
    const parsedGoals = coreGoals.trim()
      ? coreGoals.split(",").map((s) => Number(s.trim())).filter((n) => Number.isInteger(n))
      : null;

    const body: Record<string, unknown> = {
      title: title.trim(),
      shortDescription: shortDescription.trim() || null,
      longDescription: longDescription.trim() || null,
      imagePath: imagePath.trim() || null,
      periodText: periodText.trim() || null,
      durationText: durationText.trim() || null,
      locationText: locationText.trim() || null,
      coreGoals: parsedGoals,
      isNew, identitySensitive, oncePerYear, themaSpecifiek, isActive,
      // disciplines uses replace-semantics; always send the current set.
      disciplines,
    };

    if (hasOffering) {
      if (enrolmentModeCode) body.enrolmentModeCode = enrolmentModeCode;
      if (decisionStyleCode) body.decisionStyleCode = decisionStyleCode;
      if (datePreferencePolicyCode) body.datePreferencePolicyCode = datePreferencePolicyCode;
      if (addonPolicyCode) body.addonPolicyCode = addonPolicyCode;
      body.selectionPrompt = selectionPrompt.trim() || null;
      body.requiredYearLevels = requiredYearLevels.length ? requiredYearLevels : null;
      body.participatingYearLevels = participatingYearLevels.length ? participatingYearLevels : null;
      body.maxYearLevels = maxYearLevels.length ? maxYearLevels : null;
      body.vastStackable = vastStackable;
      body.techKwadraat = techKwadraat;
      body.offeringYearLevels = offeringYearLevels;
      body.choiceSetId = choiceSetId === NO_CHOICE_SET ? null : Number(choiceSetId);

      // Date blocks: keep rows with both dates filled; replace-semantics.
      body.dateBlocks = dateBlocks
        .filter((b) => b.startDate && b.endDate)
        .map((b) => ({
          startDate: b.startDate,
          endDate: b.endDate,
          label: b.label.trim() || null,
        }));

      // Add-ons: keep rows with a label; derive a stable addonCode from the label (or index).
      // Codes must be unique within the offering, so de-duplicate by suffixing the index.
      const usedCodes = new Set<string>();
      body.addons = addons
        .filter((a) => a.label.trim())
        .map((a, i) => {
          let code = a.addonCode.trim() || slugify(a.label) || `addon-${i + 1}`;
          if (usedCodes.has(code)) code = `${code}-${i + 1}`;
          usedCodes.add(code);
          return {
            addonCode: code,
            label: a.label.trim(),
            description: a.description.trim() || null,
          };
        });

      // Visibility per school (replace-semantics). When the restrict toggle is off the
      // activity is visible to all schools, so we send empty lists regardless of what's still
      // held in memory. When on, persist the selected whitelist/blacklist.
      body.allowSchoolIds = restrictVisibility ? allowSchoolIds : [];
      body.blockSchoolIds = restrictVisibility ? blockSchoolIds : [];
    }

    try {
      const res = await (api.api.admin.programmes as any)[":id"].full.$put({ param: { id: programmeId }, json: body });
      if (res.ok) {
        setSaveState("saved");
        savedToast.show();
        onSaved?.();
      } else {
        let msg = "Opslaan mislukt. Controleer de invoer.";
        try {
          const err = await res.json();
          if (err?.error === "no_offering") msg = "Deze activiteit heeft nog geen aanbod om in te stellen.";
          else if (err?.error === "invalid_reference") msg = "Onbekende code (leerjaar, discipline of keuzegroep).";
          else if (err?.error === "duplicate") msg = "Dit bestaat al (dubbele waarde).";
        } catch { /* keep default */ }
        setSaveError(msg);
        setSaveState("error");
      }
    } catch {
      setSaveError("Er is iets misgegaan. Probeer het opnieuw.");
      setSaveState("error");
    }
  }

  if (loading) return <p className="text-muted-foreground">Laden...</p>;
  if (loadError) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-destructive">{loadError}</p>
        <Button variant="outline" onClick={onClose}>Sluiten</Button>
      </div>
    );
  }

  const lookups = data.lookups ?? {};
  const allYearLevels: string[] = lookups.yearLevels ?? [];
  const enrolmentModes: string[] = lookups.enrolmentModes ?? [];
  const allDisciplines: string[] = lookups.disciplines ?? [];
  const choiceSets: { id: number; code: string; title: string }[] = lookups.choiceSets ?? [];
  const allSchools: { id: number; name: string }[] = lookups.schools ?? [];
  const hasOffering = !!data.offering;

  return (
    <div className="space-y-5">
      {/* ─── Inhoud ─── */}
      <Section title="Inhoud" description="De informatie die scholen over deze activiteit te zien krijgen.">
        <Field label="Titel">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Bijv. Theaterworkshop groep 5/6" />
        </Field>
        <Field label="Korte beschrijving">
          <Textarea rows={2} value={shortDescription} onChange={(e) => setShortDescription(e.target.value)} placeholder="Een interactieve theatervoorstelling" />
        </Field>
        <Field label="Uitgebreide beschrijving">
          <Textarea value={longDescription} onChange={(e) => setLongDescription(e.target.value)} placeholder="Volledige beschrijving van de activiteit..." />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Periode" hint="Vrije tekst, bijv. 'najaar 2026'.">
            <Input value={periodText} onChange={(e) => setPeriodText(e.target.value)} placeholder="Bijv. najaar 2026" />
          </Field>
          <Field label="Duur" hint="Vrije tekst, bijv. '2 lesuren'.">
            <Input value={durationText} onChange={(e) => setDurationText(e.target.value)} placeholder="Bijv. 2 lesuren" />
          </Field>
          <Field label="Locatie" hint="Vrije tekst, bijv. 'op school' of een externe locatie.">
            <Input value={locationText} onChange={(e) => setLocationText(e.target.value)} placeholder="Bijv. op school" />
          </Field>
          <Field label="Afbeelding (pad)" hint="Pad naar de afbeelding, indien aanwezig.">
            <Input value={imagePath} onChange={(e) => setImagePath(e.target.value)} placeholder="Bijv. /images/theater.jpg" />
          </Field>
        </div>
        <Field label="Kerndoelen" hint="Nummers van kerndoelen, gescheiden door komma's. Bijv. '54, 55, 56'.">
          <Input value={coreGoals} onChange={(e) => setCoreGoals(e.target.value)} placeholder="54, 55, 56" />
        </Field>
        <div className="space-y-3 rounded-md border bg-muted/20 p-4">
          <p className="text-xs font-medium text-muted-foreground">Kenmerken</p>
          <CheckRow id="isNew" checked={isNew} onChange={setIsNew} label="Nieuw" hint="Toon een 'nieuw'-markering bij deze activiteit." />
          <CheckRow id="identitySensitive" checked={identitySensitive} onChange={setIdentitySensitive} label="Identiteitsgevoelig" hint="Relevant voor scholen met een levensbeschouwelijke identiteit." />
          <CheckRow id="oncePerYear" checked={oncePerYear} onChange={setOncePerYear} label="Eén keer per schooljaar" hint="Een groep kan dit hooguit één keer per jaar doen." />
          <CheckRow id="themaSpecifiek" checked={themaSpecifiek} onChange={setThemaSpecifiek} label="Themaspecifiek" hint="Hoort bij een specifiek thema." />
        </div>
        <div className="rounded-md border p-4">
          <CheckRow id="isActive" checked={isActive} onChange={setIsActive} label="Actief" hint="Inactieve activiteiten worden niet aan scholen getoond." />
        </div>
      </Section>

      {/* ─── Aanbod & leerjaren ─── */}
      <Section title="Aanbod & leerjaren" description={hasOffering ? undefined : "Deze activiteit heeft nog geen aanbod. Aanbod- en leerjaarvelden zijn daarom niet bewerkbaar."}>
        <Field label="Soort aanbod">
          <Select
            value={enrolmentModeCode || null}
            onValueChange={(v) => setEnrolmentModeCode((v as string) ?? "")}
            disabled={!hasOffering}
          >
            <SelectTrigger>
              <span className={`flex-1 truncate ${enrolmentModeCode ? "" : "text-muted-foreground"}`}>
                {labelFromMap(ENROLMENT_MODE_LABELS, enrolmentModeCode) ?? "Kies..."}
              </span>
            </SelectTrigger>
            <SelectPopup>
              {enrolmentModes.map((code) => (
                <SelectItem key={code} value={code}>{ENROLMENT_MODE_LABELS[code] ?? code}</SelectItem>
              ))}
            </SelectPopup>
          </Select>
        </Field>

        <Field label="Disciplines" hint="Onder welke disciplines valt deze activiteit?">
          <div className="flex flex-wrap gap-2">
            {allDisciplines.map((code) => {
              const on = disciplines.includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => toggle(disciplines, setDisciplines, code)}
                  className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                    on ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent/40"
                  }`}
                >
                  {DISCIPLINE_LABELS[code] ?? code}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Voor welke leerjaren?" hint="De leerjaren waarvoor deze activiteit bedoeld is.">
          <div className={hasOffering ? "" : "pointer-events-none opacity-60"}>
            <YearGrid all={allYearLevels} selected={offeringYearLevels} onToggle={(c) => toggle(offeringYearLevels, setOfferingYearLevels, c)} />
          </div>
        </Field>

        <Field
          label="Welke leerjaren mogen meedoen in een combigroep?"
          hint="Leeg laten = zelfde als hierboven. Vul alleen in als een combigroep met andere leerjaren mag meedoen."
        >
          <div className={hasOffering ? "" : "pointer-events-none opacity-60"}>
            <YearGrid all={allYearLevels} selected={participatingYearLevels} onToggle={(c) => toggle(participatingYearLevels, setParticipatingYearLevels, c)} />
          </div>
        </Field>

        <Field
          label="Alleen voor groepen met leerjaar..."
          hint="Strikte eis: de groep moet ten minste één van deze leerjaren bevatten. Leeg = geen extra eis."
        >
          <div className={hasOffering ? "" : "pointer-events-none opacity-60"}>
            <YearGrid all={allYearLevels} selected={requiredYearLevels} onToggle={(c) => toggle(requiredYearLevels, setRequiredYearLevels, c)} />
          </div>
        </Field>

        <Field
          label="De groep mag alleen uit deze leerjaren bestaan"
          hint="Strikte eis: de groep mag uitsluitend uit deze leerjaren bestaan (exacte samenstelling). Leeg = geen eis."
        >
          <div className={hasOffering ? "" : "pointer-events-none opacity-60"}>
            <YearGrid all={allYearLevels} selected={maxYearLevels} onToggle={(c) => toggle(maxYearLevels, setMaxYearLevels, c)} />
          </div>
        </Field>
      </Section>

      {/* ─── Keuzegedrag ─── */}
      <Section title="Keuzegedrag" description={hasOffering ? "Hoe maken scholen hun keuze voor deze activiteit?" : "Niet bewerkbaar zonder aanbod."}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Manier van kiezen">
            <Select value={decisionStyleCode || null} onValueChange={(v) => setDecisionStyleCode((v as string) ?? "")} disabled={!hasOffering}>
              <SelectTrigger>
                <span className={`flex-1 truncate ${decisionStyleCode ? "" : "text-muted-foreground"}`}>
                  {DECISION_STYLE.find((d) => d.code === decisionStyleCode)?.label ?? "Kies..."}
                </span>
              </SelectTrigger>
              <SelectPopup>
                {DECISION_STYLE.map((d) => <SelectItem key={d.code} value={d.code}>{d.label}</SelectItem>)}
              </SelectPopup>
            </Select>
          </Field>
          <Field label="Datumvoorkeur">
            <Select value={datePreferencePolicyCode || null} onValueChange={(v) => setDatePreferencePolicyCode((v as string) ?? "")} disabled={!hasOffering}>
              <SelectTrigger>
                <span className={`flex-1 truncate ${datePreferencePolicyCode ? "" : "text-muted-foreground"}`}>
                  {DATE_PREFERENCE_POLICY.find((d) => d.code === datePreferencePolicyCode)?.label ?? "Kies..."}
                </span>
              </SelectTrigger>
              <SelectPopup>
                {DATE_PREFERENCE_POLICY.map((d) => <SelectItem key={d.code} value={d.code}>{d.label}</SelectItem>)}
              </SelectPopup>
            </Select>
          </Field>
          <Field label="Extra's">
            <Select value={addonPolicyCode || null} onValueChange={(v) => setAddonPolicyCode((v as string) ?? "")} disabled={!hasOffering}>
              <SelectTrigger>
                <span className={`flex-1 truncate ${addonPolicyCode ? "" : "text-muted-foreground"}`}>
                  {ADDON_POLICY.find((d) => d.code === addonPolicyCode)?.label ?? "Kies..."}
                </span>
              </SelectTrigger>
              <SelectPopup>
                {ADDON_POLICY.map((d) => <SelectItem key={d.code} value={d.code}>{d.label}</SelectItem>)}
              </SelectPopup>
            </Select>
          </Field>
          <Field label="Hoort bij keuzegroep (kies 1)" hint="Activiteiten in dezelfde keuzegroep concurreren om dezelfde keuze.">
            <Select value={choiceSetId} onValueChange={(v) => setChoiceSetId((v as string) ?? NO_CHOICE_SET)} disabled={!hasOffering}>
              <SelectTrigger>
                <span className="flex-1 truncate">
                  {choiceSetId === NO_CHOICE_SET
                    ? "Geen keuzegroep"
                    : (choiceSets.find((s) => String(s.id) === choiceSetId)?.title ?? `#${choiceSetId}`)}
                </span>
              </SelectTrigger>
              <SelectPopup>
                <SelectItem value={NO_CHOICE_SET}>Geen keuzegroep</SelectItem>
                {choiceSets.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.title}</SelectItem>)}
              </SelectPopup>
            </Select>
          </Field>
        </div>
        <Field label="Toelichting bij de keuze" hint="Optionele uitleg die scholen bij het kiezen te zien krijgen.">
          <Textarea value={selectionPrompt} onChange={(e) => setSelectionPrompt(e.target.value)} disabled={!hasOffering} placeholder="Bijv. Kies één voorstelling voor de groepen 5 en 6." />
        </Field>
        <div className={`space-y-3 rounded-md border p-4 ${hasOffering ? "" : "pointer-events-none opacity-60"}`}>
          <CheckRow
            id="vastStackable"
            checked={vastStackable}
            onChange={setVastStackable}
            label="Mag samen met andere vaste voorstellingen"
            hint="Standaard doet een groep per leerjaar maar een vaste voorstelling. Vink dit aan voor uitzonderingen (zoals groep 5) die naast een andere vaste voorstelling mogen."
          />
          <CheckRow
            id="techKwadraat"
            checked={techKwadraat}
            onChange={setTechKwadraat}
            label="Onderdeel van TechKwadraat (eigen bijdrage)"
            hint="Vink dit aan voor activiteiten uit het TechKwadraat-aanbod waarvoor de school een eigen bijdrage betaalt."
          />
        </div>
      </Section>

      {/* ─── Zichtbaarheid voor scholen ─── */}
      <Section
        title="Zichtbaarheid voor scholen"
        description={hasOffering ? "Standaard is deze activiteit voor alle scholen zichtbaar. Beperk dat hier wanneer dat nodig is." : "Niet bewerkbaar zonder aanbod."}
      >
        {!hasOffering ? (
          <p className="text-sm text-muted-foreground">Deze activiteit heeft nog geen aanbod om de zichtbaarheid voor in te stellen.</p>
        ) : (
          <div className="space-y-6">
            <div className="rounded-md border p-4">
              <CheckRow
                id="restrictVisibility"
                checked={restrictVisibility}
                onChange={setRestrictVisibility}
                label="Zichtbaarheid beperken tot bepaalde scholen"
                hint="Laat dit uit om de activiteit voor alle scholen te tonen. Zet het aan om per school in te stellen wie de activiteit wel of niet ziet."
              />
            </div>
            {restrictVisibility && (
              <>
                <Field
                  label="Alleen zichtbaar voor deze scholen"
                  hint="Leeg laten = zichtbaar voor alle scholen. Vink scholen aan om de activiteit uitsluitend aan die scholen te tonen."
                >
                  <SchoolMultiselect
                    schools={allSchools}
                    selected={allowSchoolIds}
                    onToggle={(sid) => toggleId(allowSchoolIds, setAllowSchoolIds, sid)}
                  />
                </Field>
                <Field
                  label="Verbergen voor deze scholen"
                  hint="Aangevinkte scholen zien deze activiteit niet, ook niet wanneer hierboven niets is gekozen."
                >
                  <SchoolMultiselect
                    schools={allSchools}
                    selected={blockSchoolIds}
                    onToggle={(sid) => toggleId(blockSchoolIds, setBlockSchoolIds, sid)}
                  />
                </Field>
              </>
            )}
          </div>
        )}
      </Section>

      {/* ─── Datums & extra's ─── */}
      <Section
        title="Datums & extra's"
        description={hasOffering ? "De periodes waarin deze activiteit loopt, en eventuele extra's die scholen kunnen aanvinken." : "Niet bewerkbaar zonder aanbod."}
      >
        {!hasOffering ? (
          <p className="text-sm text-muted-foreground">Deze activiteit heeft nog geen aanbod om datums of extra's voor in te stellen.</p>
        ) : (
          <div className="space-y-6">
            {/* Date-ranges (date_blocks) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Periodes</Label>
                <Button type="button" variant="outline" size="sm" onClick={addDateBlock}>
                  <PlusIcon size={14} /> Periode toevoegen
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Periodes bepalen binnen welke termijn een school een voorkeur kan aangeven. De geldende datumvoorkeur ({DATE_PREFERENCE_POLICY.find((d) => d.code === datePreferencePolicyCode)?.label ?? "—"}) stelt u hierboven in.
              </p>
              {dateBlocks.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nog geen periodes; voeg er een toe om een termijn vast te leggen.</p>
              ) : (
                <div className="space-y-2">
                  {dateBlocks.map((row, i) => (
                    <div key={i} className="flex flex-wrap items-end gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Van</Label>
                        <Input type="date" value={row.startDate} onChange={(e) => updateDateBlock(i, { startDate: e.target.value })} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Tot</Label>
                        <Input type="date" value={row.endDate} onChange={(e) => updateDateBlock(i, { endDate: e.target.value })} />
                      </div>
                      <div className="flex-1 space-y-1" style={{ minWidth: 160 }}>
                        <Label className="text-xs">Label (optioneel)</Label>
                        <Input value={row.label} onChange={(e) => updateDateBlock(i, { label: e.target.value })} placeholder="Bijv. blok 1" />
                      </div>
                      <Button type="button" variant="outline" size="icon" onClick={() => removeDateBlock(i)} aria-label="Verwijder rij">
                        <Trash2Icon size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add-ons */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Extra's</Label>
                <Button type="button" variant="outline" size="sm" onClick={addAddon}>
                  <PlusIcon size={14} /> Extra toevoegen
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Optionele extra's die een school later kan aanvinken, bijvoorbeeld een gastles of een rondleiding.
              </p>
              {addons.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nog geen extra's; voeg er een toe als scholen iets kunnen bijboeken.</p>
              ) : (
                <div className="space-y-2">
                  {addons.map((row, i) => (
                    <div key={i} className="flex flex-wrap items-end gap-2">
                      <div className="flex-1 space-y-1" style={{ minWidth: 160 }}>
                        <Label className="text-xs">Naam</Label>
                        <Input value={row.label} onChange={(e) => updateAddon(i, { label: e.target.value })} placeholder="Bijv. Gastles" />
                      </div>
                      <div className="flex-1 space-y-1" style={{ minWidth: 200 }}>
                        <Label className="text-xs">Toelichting (optioneel)</Label>
                        <Input value={row.description} onChange={(e) => updateAddon(i, { description: e.target.value })} placeholder="Korte uitleg" />
                      </div>
                      <Button type="button" variant="outline" size="icon" onClick={() => removeAddon(i)} aria-label="Verwijder rij">
                        <Trash2Icon size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Section>

      {saveError && <p className="text-sm text-destructive">{saveError}</p>}

      {/* Action bar */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          {saveState === "saving" && <><Loader2Icon size={15} className="animate-spin text-muted-foreground" /><span className="text-muted-foreground">Opslaan...</span></>}
          {saveState === "saved" && <><CheckCircle2Icon size={15} className="text-emerald-600" /><span className="text-emerald-700">Opgeslagen</span></>}
          {saveState === "error" && <><AlertCircleIcon size={15} className="text-red-600" /><span className="text-red-700">Niet opgeslagen</span></>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onClose}>Sluiten</Button>
          <Button onClick={handleSave} disabled={saveState === "saving" || !title.trim()}>
            {saveState === "saving" ? "Bezig..." : "Wijzigingen opslaan"}
          </Button>
        </div>
      </div>

      {savedToast.toast}
    </div>
  );
}
